import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ActivityMetrics } from 'components/SaloonLobby/types';

interface ActivityStripState {
  // null until the first successful activity-strip CDN read arrives — the
  // lobby shows an explicit loading placeholder while this is empty, and
  // keeps showing the last-set value (flagged stale, not hidden) once it
  // ages past its threshold (see SaloonLobbyContainer).
  metrics: ActivityMetrics | null;
  // The DOCUMENT's own generatedAt (server truth, ms since epoch) — NOT
  // Date.now() at dispatch time. Using receive-time here would double-count
  // staleness: useActivityStripFeed already rejects a document whose
  // generatedAt is too old, so stamping "now" on top of that document's
  // already-aged timestamp would let a reading sit for up to
  // deriveStaleAfterMs twice over before this state's own staleness check
  // (below) ever caught it. Persists across a Lobby/GuestLobby
  // unmount+remount (e.g. leaving to play a hand and returning to the
  // lobby), unlike useActivityStripFeed's own local state — SaloonLobbyContainer
  // uses this to flag a value old enough that it may no longer be accurate
  // (shown as 'stale', not hidden — see that component's own comment),
  // rather than confidently displaying it with no signal that the feed has
  // actually gone quiet in the meantime.
  updatedAt: number | null;
  // The publisher's own cadence (ms), as published in the CDN document's
  // `maxAgeMs` field and resolved by useActivityStripFeed (falls back to
  // DEFAULT_MAX_AGE_MS when absent/malformed). SaloonLobbyContainer derives
  // its staleness threshold from this via deriveStaleAfterMs, instead of a
  // hand-copied constant that could drift out of sync with a publisher-side
  // cadence change. null alongside metrics: null, before the first read.
  maxAgeMs: number | null;
  // useActivityStripFeed's own clockOffsetMs for the read that produced
  // `updatedAt` — see that field's comment. SaloonLobbyContainer's
  // isActivityStale must add this to its own Date.now() reading before
  // comparing against `updatedAt` (server truth), or a skewed device clock
  // makes that comparison wrong in either direction. 0 (no correction)
  // before the first read.
  clockOffsetMs: number;
  // Incremented by requestActivityStripRetry() when the player uses the
  // 'unavailable' status's Retry action (ActivityStrip.tsx). useActivityStripDispatch
  // reads this and passes it straight through as useActivityStripFeed's
  // retrySignal, whose effect restarts (cancelling any in-flight/backed-off
  // poll and firing an immediate one) whenever this value changes — see that
  // hook's own comment. A counter, not a boolean: two retries in a row (the
  // first one also fails) must each produce a new value.
  retryToken: number;
}

const initialState: ActivityStripState = {
  metrics: null,
  updatedAt: null,
  maxAgeMs: null,
  clockOffsetMs: 0,
  retryToken: 0
};

export const activityStripSlice = createSlice({
  name: 'activityStrip',
  initialState,
  reducers: {
    // Every field is supplied by the caller rather than read here (e.g.
    // `updatedAt` is the document's own generatedAtMs, not Date.now() at
    // dispatch time — see this state's own field comments above), so this
    // reducer stays a pure function of its inputs — trivial to test without
    // faking the clock inside the slice.
    setActivityStrip: (
      state,
      action: PayloadAction<{
        metrics: ActivityMetrics;
        updatedAt: number;
        maxAgeMs: number;
        clockOffsetMs: number;
      }>
    ) => {
      state.metrics = action.payload.metrics;
      state.updatedAt = action.payload.updatedAt;
      state.maxAgeMs = action.payload.maxAgeMs;
      state.clockOffsetMs = action.payload.clockOffsetMs;
    },
    // Dispatched by ActivityStrip's Retry action (only rendered while status
    // is 'unavailable'). Only bumps the counter — it has no opinion on
    // whether the retry actually succeeds; the next successful
    // useActivityStripFeed read still goes through setActivityStrip above
    // like any other poll.
    requestActivityStripRetry: (state) => {
      state.retryToken += 1;
    }
  }
});

export const { setActivityStrip, requestActivityStripRetry } = activityStripSlice.actions;

export default activityStripSlice.reducer;
